import { assertNever } from "../lib/assertNever";
import type { ZwParagraphFormatting } from "../lib/zwConfig";

/** First-line indent for "indented" paragraphs, in the editor's units. */
export const PARAGRAPH_INDENT_EM = 1.5;

/** Raised initial / drop cap sizes, as in the editor (`prose.css`). */
export const RAISED_INITIAL_EM = 2.4;
export const DROP_CAP_EM = 3.3;

/** Paragraphs that never take a first-line indent. */
const NOT_INDENTED = [
  ".zw-scene-break",
  ".zw-chapter-opening",
  ".zw-flush-first",
  '[style*="text-align: center"]',
  '[style*="text-align: right"]',
  ":has(img)",
]
  .map((selector) => `:not(${selector})`)
  .join("");

function paragraphCss(formatting: ZwParagraphFormatting): string {
  switch (formatting.style) {
    case "indented": {
      // Scene first paragraphs carry `zw-flush-first` (`markSceneOpening`);
      // "sceneFlush" also starts one after a heading or scene break flush.
      const indented =
        formatting.firstParagraph === "sceneFlush"
          ? `.chapter p:not(.zw-scene-break) + p${NOT_INDENTED}`
          : `.chapter p${NOT_INDENTED}`;
      return `.chapter p:not(.zw-scene-break) {
  text-indent: 0;
  margin: 0 !important;
}

${indented} {
  text-indent: ${PARAGRAPH_INDENT_EM}em;
}
`;
    }
    case "spaced":
      return `.chapter p:not(.zw-scene-break) {
  text-indent: 0;
  margin: 0 0 0.85em !important;
}
`;
    default:
      return assertNever(formatting.style);
  }
}

/**
 * Chapter paragraph CSS for the PDF and EPUB books (Styling ▸ Paragraphs),
 * matching the editor. "indented": paragraphs set together with the first
 * line indented, except centred / right-aligned ones, images and a flush
 * first paragraph ("chapterFlush": a chapter's first; "sceneFlush": each
 * scene's first and any after a heading, scene break or other block). "spaced": a gap and no indent. Then the chapter
 * opening (`markSceneOpening`). Scoped to `.chapter` so title / copyright
 * pages keep their own layout; scene breaks keep their own spacing.
 */
export function chapterParagraphCss(formatting: ZwParagraphFormatting): string {
  return `${paragraphCss(formatting)}
.zw-opening-drop {
  float: left;
  font-size: ${DROP_CAP_EM}em;
  line-height: 0.85;
  padding: 0.05em 0.08em 0 0;
}

.zw-opening-raised {
  font-size: ${RAISED_INITIAL_EM}em;
  line-height: 1;
}

.zw-opening-lead {
  font-variant: small-caps;
  font-synthesis-small-caps: auto;
  letter-spacing: 0.04em;
}
`;
}
